import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DEFAULT_INACTIVITY_TIMEOUT } from '../constants';

interface UseInactivityTimerOptions {
  timeout?: number; // milliseconds
  events?: string[];
  enabled?: boolean;
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
  'keydown'
];

const useInactivityTimer = (
  onInactive: () => void,
  options: UseInactivityTimerOptions = {}
) => {
  const {
    timeout = DEFAULT_INACTIVITY_TIMEOUT,
    events,
    enabled = true
  } = options;

  const stableEvents = useMemo(() => events ?? DEFAULT_EVENTS, [events]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveRef = useRef(true);
  const onInactiveRef = useRef(onInactive);

  useEffect(() => {
    onInactiveRef.current = onInactive;
  }, [onInactive]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    isActiveRef.current = true;

    timerRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        isActiveRef.current = false;
        onInactiveRef.current();
      }
    }, timeout);
  }, [enabled, timeout]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const handleActivity = () => {
      resetTimer();
    };

    stableEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    resetTimer();

    return () => {
      stableEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [enabled, resetTimer, stableEvents]);

  return {
    resetTimer,
    isActive: isActiveRef.current
  };
};

export default useInactivityTimer;
